import React from 'react';

const heading = 'mt-8 text-xl font-bold tracking-tight text-zinc-950 dark:text-zinc-50';
const paragraph = 'my-3 leading-7 text-zinc-600 dark:text-zinc-300';
const list = 'my-3 list-disc space-y-2 pl-6 leading-7 text-zinc-600 dark:text-zinc-300';
const link = 'font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-300';

const About: React.FC = () => (
  <main className="min-h-screen bg-white px-6 py-14 font-sans dark:bg-zinc-950">
    <article className="mx-auto max-w-[860px]">
      <a
        href="/"
        className="mb-8 inline-flex text-sm font-semibold text-brand-700 hover:text-brand-900 dark:text-brand-300 dark:hover:text-brand-100"
      >
        ← Peckle
      </a>
      <h1 className="text-4xl font-extrabold tracking-tight text-zinc-950 dark:text-zinc-50">About Peckle</h1>

      <p className={paragraph}>
        Peckle is an AI product ad maker for people who sell things. A seller photographs an item on a
        phone, says what they want in plain language, and Peckle returns a finished product photo or
        promotional post — background removed, lighting corrected, price and branding applied — ready to
        publish to WhatsApp Status, Instagram, a storefront listing, or a paid ad.
      </p>

      <h2 className={heading}>The company</h2>
      <p className={paragraph}>
        Peckle is built and operated by Gotok Technologies Limited ("Gotok"), an independent software
        company. Gotok owns the Peckle brand, publishes the Peckle apps on the Apple App Store and Google
        Play, and operates the web application at{' '}
        <a href="https://app.peckle.ai" className={link}>app.peckle.ai</a>. Gotok is the advertiser behind
        all Peckle marketing.
      </p>

      <h2 className={heading}>Who uses Peckle</h2>
      <p className={paragraph}>
        Our customers are small and micro sellers who market their own inventory, typically without a
        designer, a studio, or a marketing budget. In practice that means market traders, thrift and
        fashion resellers, bakers and home-food sellers, jewellery and accessory makers, salons, and
        small independent shops. Peckle is available worldwide, with active product and marketing focus
        in Nigeria, India, Brazil, and the United States, and is localised into nine languages.
      </p>
      <p className={paragraph}>
        Peckle runs on iOS, Android, and in the browser. The browser version exists because a meaningful
        share of our users are on shared or storage-constrained devices where installing an app is a real
        obstacle.
      </p>

      <h2 className={heading}>How Peckle makes money</h2>
      <p className={paragraph}>
        Peckle is free to start. New users can create and export work without paying, which is
        deliberate: our customers generally will not pay before they have seen a usable result from their
        own product photo. Revenue comes from two sources layered on top of that free tier.
      </p>
      <ul className={list}>
        <li>
          <strong>Subscriptions.</strong> Paid plans (Plus and Pro) are billed monthly or annually and
          unlock watermark-free exports, high-resolution renders, higher brand-kit limits, and bonus
          credits on top-ups.
        </li>
        <li>
          <strong>Credit packs.</strong> One-off consumable purchases for users who need additional AI
          generations without committing to a recurring plan.
        </li>
      </ul>
      <p className={paragraph}>
        Purchases are processed by the Apple App Store and Google Play on mobile, and by our payment
        processor for web and direct checkout. Pricing is set per market rather than converted at
        checkout, so prices differ by country. Current prices are shown in the app before purchase.
      </p>

      <h2 className={heading}>How we advertise Peckle</h2>
      <p className={paragraph}>
        Gotok markets Peckle directly. We run app-install and web-traffic campaigns for the Peckle apps on
        advertising platforms including Google Ads and Meta, paid for by Gotok and promoting only our own
        product. Because we operate in several markets and several languages at once, we maintain internal
        tooling that generates localised ad creatives from a shared configuration, launches and updates
        campaigns, manages budgets, and reports performance back to us.
      </p>
      <p className={paragraph}>
        That tooling is used exclusively by Gotok staff to manage Gotok's own advertising accounts. We do
        not manage advertising on behalf of third parties, we do not resell advertising services or
        platform access, and the tooling is not offered to customers or made available outside the
        company.
      </p>

      <h2 className={heading}>Contact</h2>
      <p className={paragraph}>
        General and business enquiries:{' '}
        <a href="mailto:onye@gotok.app" className={link}>onye@gotok.app</a>. Customer support:{' '}
        <a href="mailto:support@peckle.ai" className={link}>support@peckle.ai</a>. Privacy requests:{' '}
        <a href="mailto:privacy@peckle.ai" className={link}>privacy@peckle.ai</a>.
      </p>
      <p className={paragraph}>
        See also our <a href="/terms" className={link}>Terms of Service</a> and{' '}
        <a href="/privacy" className={link}>Privacy Policy</a>.
      </p>
    </article>
  </main>
);

export default About;
